/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */
import { useMutation } from '@tanstack/react-query';
import React, { FC, useState, useEffect, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Redirect, Switch, Route, useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import DelayedShipmentWarningModal from 'src/components/DelayedShipmentWarningModal';
import { withPimURL } from 'src/helpers/service';
import { useSkipAutoPopups } from 'src/hooks/useSkipAutoPopups';
import Maintenance from 'src/pages/maintenance';
import { useDelayedShipmentCheck } from 'src/services/hooks/useDelayedShipmentCheck';
import { RootState, useAppDispatch, useAppSelector } from 'src/services/redux/app/Store';
import { announcementUnRead } from 'src/services/redux/slices/announcement/announcement-slice';
import { _login } from 'src/services/redux/slices/auth/auth-slice';
import { MasterLayout } from '../_metronic/layout/MasterLayout';
import { Logout, AuthPage } from '../modules/auth';
import { AppDownloadRedirect } from '../modules/auth/components/AppDownloadRedirect';
import { PrivateRoutes } from './PrivateRoutes';

// Geciken kargo uyarısı gösterilecek sayfalar
const DELAYED_SHIPMENT_WARNING_PAGES = ['/orders/orders-management/all', '/products/product-operations', '/inventory/all'];

const Routes: FC = () => {
  const dispatch = useAppDispatch();
  const { maintenance } = useAppSelector((state) => state.maintenanceList, shallowEqual);
  const auth: any = useSelector<RootState>((state) => state.auth, shallowEqual);
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(history.location.search);
  const refreshToken = searchParams.get('r');
  const PLATFORM = process.env.REACT_APP_PLATFORM;
  const [showDelayedShipmentModal, setShowDelayedShipmentModal] = useState(false);
  const skipAutoPopups = useSkipAutoPopups();

  // Geciken kargo kontrolü
  const { hasDelayedShipment, isSuccess: delayedShipmentCheckSuccess } = useDelayedShipmentCheck();

  // Mevcut sayfanın geciken kargo uyarısı gösterilecek sayfalardan biri olup olmadığını kontrol et
  const isOnDelayedShipmentWarningPage = useMemo(() => {
    return DELAYED_SHIPMENT_WARNING_PAGES.some((page) => location.pathname.startsWith(page));
  }, [location.pathname]);

  const refreshTokenMutate = useMutation({
    mutationKey: ['refresh-token'],
    mutationFn: async () => {
      const { headers, ip = '' } = await axios.get(withPimURL('/vendor/my-ip')).then((res) => res.data);
      const clientIp = headers?.['client-ip']?.[0] || ip || '';
      return axios.post(`${process.env.REACT_APP_API_AUTH_URL}/login-with-refresh`, { platform: PLATFORM, refreshToken, clientIp: ip });
    },
    onSuccess: (data: any) => {
      const searchParams = new URLSearchParams(history.location.search);
      const redirect = searchParams.get('redirect');
      dispatch(_login(data.data));
      dispatch(announcementUnRead());
      if (redirect && redirect !== 'logout') history.push(`/${redirect}`);
    },
    onError: () => {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.delete('r');
      const search = searchParams.toString();
      history.push({ pathname: '/auth/login', search: search ? `?${search}` : '' });
      toast.error('Giriş başarısız.');
    },
  });

  // Geciken kargo uyarı modal'ı kontrolü - her sayfa girişinde göster (geciken sipariş varsa)
  useEffect(() => {
    if (auth?.user && isOnDelayedShipmentWarningPage && delayedShipmentCheckSuccess && hasDelayedShipment && !skipAutoPopups) {
      setShowDelayedShipmentModal(true);
    } else {
      setShowDelayedShipmentModal(false);
    }
  }, [auth?.user, isOnDelayedShipmentWarningPage, delayedShipmentCheckSuccess, hasDelayedShipment, skipAutoPopups]);

  if (refreshToken && !refreshTokenMutate.isLoading && !refreshTokenMutate.isSuccess && !auth?.user) refreshTokenMutate.mutate();

  if (!maintenance) {
    return (
      <Switch>
        <Route>
          <Maintenance />
        </Route>
      </Switch>
    );
  }
  return (
    <>
      <Switch>
        {/* Uygulama indirme yönlendirmesi (platforma göre). Auth durumundan bağımsız, herkese açık. */}
        <Route path="/app" exact component={AppDownloadRedirect} />

        {!auth?.user ? (
          /*Render auth page when user at `/auth` and not authorized.*/
          <Route>
            <AuthPage />
          </Route>
        ) : (
          /*Otherwise redirect to root page (`/`)*/
          <Redirect from="/auth" to="/" />
        )}

        <Route path="/logout" component={Logout} />

        {!auth?.user ? (
          /*Redirect to `/auth` when user is not authorized*/
          <Redirect to="/auth/login" />
        ) : (
          <MasterLayout>
            <PrivateRoutes />
            <DelayedShipmentWarningModal show={showDelayedShipmentModal} handleClose={() => setShowDelayedShipmentModal(false)} />
          </MasterLayout>
        )}
      </Switch>
    </>
  );
};

export { Routes };
